<template>
  <div class="ai-assistant" data-vera-exclude>
    <v-btn
      color="red"
      icon
      large
      :class="['assistant-toggle', { 'assistant-toggle--hidden': expanded }]"
      elevation="10"
      :aria-label="expanded ? 'Close Reckon AI assistant' : 'Open Reckon AI assistant'"
      :aria-expanded="String(expanded)"
      aria-controls="vera-assistant-window"
      @click="toggleChat"
    >
      <v-icon>{{ expanded ? 'mdi-close' : 'mdi-robot-outline' }}</v-icon>
    </v-btn>

    <transition name="slide-up">
      <v-card
        v-if="expanded"
        id="vera-assistant-window"
        class="assistant-window"
        dark
        elevation="12"
        role="dialog"
        aria-label="Vera, the Reckon AI assistant"
      >
        <v-card-title class="assistant-header">
          <span class="assistant-header__brand">
            <span class="assistant-header__mark" aria-hidden="true">
              <v-icon>mdi-robot-outline</v-icon>
            </span>
            <span class="assistant-header__identity">
              <small>Reckon // Intelligence</small>
              <strong>Vera</strong>
            </span>
          </span>
          <span class="assistant-header__tools">
            <span class="assistant-status"><i /> Online</span>
            <v-btn class="assistant-header__button" icon small aria-label="Start a new conversation" @click="clearConversation">
              <v-icon small>mdi-refresh</v-icon>
            </v-btn>
            <v-btn class="assistant-header__button" icon small aria-label="Close Vera" @click="toggleChat">
              <v-icon>mdi-chevron-down</v-icon>
            </v-btn>
          </span>
        </v-card-title>

        <v-divider />

        <v-card-text class="assistant-body" :aria-busy="String(loading)">
          <div ref="messagesContainer" class="messages" role="log" aria-live="polite">
            <article
              v-for="message in messages"
              :key="message.id"
              :class="['message-row', `message-row--${message.sender}`]"
            >
              <div :class="['message', message.sender]">{{ message.text }}</div>
              <div
                v-if="message.sender === 'assistant' && message.answerKind === 'general'"
                class="answer-label"
              >
                <v-icon x-small>mdi-information-outline</v-icon>
                General information · no live web search
              </div>
              <div v-if="message.sources && message.sources.length" class="message-sources">
                <span class="message-sources__title">Sources</span>
                <template v-for="(source, sourceIndex) in message.sources">
                  <a
                    v-if="sourceHref(source)"
                    :key="`${message.id}-${sourceIndex}`"
                    class="message-source"
                    :href="sourceHref(source)"
                    :target="isInternalSource(source) ? null : '_blank'"
                    :rel="isInternalSource(source) ? null : 'noopener noreferrer'"
                    @click="openSource(source, $event)"
                  >
                    {{ sourceLabel(source) }}
                  </a>
                  <span
                    v-else
                    :key="`${message.id}-${sourceIndex}`"
                    class="message-source message-source--plain"
                  >
                    {{ sourceLabel(source) }}
                  </span>
                </template>
              </div>
            </article>

            <div v-if="loading" class="message-row message-row--assistant">
              <div class="message assistant assistant-thinking">
                <span></span><span></span><span></span>
                <span class="sr-only">Vera is thinking</span>
              </div>
            </div>

            <v-alert v-if="errorMessage" class="assistant-error" dense outlined type="error">
              {{ errorMessage }}
              <v-btn v-if="lastFailedQuestion" class="ml-1" small text @click="retryLastQuestion">
                Retry
              </v-btn>
            </v-alert>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="assistant-composer">
          <v-textarea
            ref="messageInput"
            v-model="userInput"
            aria-label="Ask Vera a question"
            placeholder="Ask Vera about Reckon…"
            outlined
            hide-details
            dense
            auto-grow
            rows="1"
            maxlength="1500"
            :disabled="loading"
            @keydown.enter.exact.prevent="sendMessage"
          />
          <v-btn
            icon
            color="red"
            aria-label="Send message"
            :disabled="loading || !userInput.trim()"
            @click="sendMessage"
          >
            <v-icon>mdi-send</v-icon>
          </v-btn>
        </v-card-actions>
        <p class="assistant-disclaimer">Vera can make mistakes. Confirm important details with Reckon.</p>
      </v-card>
    </transition>
  </div>
</template>

<script>
import { answerWithVera } from '@/services/vera';

const SESSION_KEY = 'reckon:vera:messages:v2';
const MAX_STORED_MESSAGES = 24;

const welcomeMessage = () => ({
  id: 'vera-welcome',
  text: 'Hi! I’m Vera. Ask me about Reckon, our people, capabilities, culture, and stories.',
  sender: 'assistant',
  answerKind: 'reckon',
  sources: [],
});

function messageId() {
  return `vera-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function restoreMessages() {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(SESSION_KEY));
    if (!Array.isArray(stored)) return [welcomeMessage()];
    const valid = stored.filter((message) => (
      message
      && ['user', 'assistant'].includes(message.sender)
      && typeof message.text === 'string'
      && message.text.trim()
    )).slice(-MAX_STORED_MESSAGES);
    return valid.length ? valid : [welcomeMessage()];
  } catch (error) {
    return [welcomeMessage()];
  }
}

export default {
  name: 'AIAssistant',
  data() {
    return {
      expanded: false,
      userInput: '',
      messages: restoreMessages(),
      loading: false,
      errorMessage: '',
      lastFailedQuestion: '',
    };
  },
  watch: {
    messages: {
      deep: true,
      handler(messages) {
        try {
          window.sessionStorage.setItem(
            SESSION_KEY,
            JSON.stringify(messages.slice(-MAX_STORED_MESSAGES)),
          );
        } catch (error) {
          // Chat continues even if browser storage is unavailable.
        }
        this.scrollToLatest();
      },
    },
  },
  methods: {
    toggleChat() {
      this.expanded = !this.expanded;
      if (this.expanded) {
        this.scrollToLatest();
        this.$nextTick(() => this.$refs.messageInput?.focus());
      }
    },
    scrollToLatest() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (!container) return;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        container.scrollTo({
          top: container.scrollHeight,
          behavior: reducedMotion ? 'auto' : 'smooth',
        });
      });
    },
    clearConversation() {
      this.messages = [welcomeMessage()];
      this.userInput = '';
      this.errorMessage = '';
      this.lastFailedQuestion = '';
      this.$nextTick(() => this.$refs.messageInput?.focus());
    },
    conversationHistory(question) {
      const history = this.messages
        .filter((message) => ['user', 'assistant'].includes(message.sender))
        .map((message) => ({ role: message.sender, content: message.text }));
      const last = history[history.length - 1];
      if (last?.role === 'user' && last.content === question) history.pop();
      return history.slice(-8);
    },
    async askQuestion(question, appendUserMessage) {
      if (this.loading || !question) return;
      const history = this.conversationHistory(question);
      if (appendUserMessage) {
        this.messages.push({
          id: messageId(),
          text: question,
          sender: 'user',
          sources: [],
        });
      }
      this.loading = true;
      this.errorMessage = '';
      this.lastFailedQuestion = '';

      try {
        const response = await answerWithVera({
          message: question,
          currentPath: this.$route.fullPath,
          history,
        });
        this.messages.push({
          id: messageId(),
          text: response.reply,
          sender: 'assistant',
          answerKind: response.answerKind,
          sources: Array.isArray(response.sources) ? response.sources : [],
        });
      } catch (error) {
        this.errorMessage = error.message || 'Vera could not answer right now.';
        this.lastFailedQuestion = question;
      } finally {
        this.loading = false;
      }
    },
    sendMessage() {
      const question = this.userInput.trim();
      if (!question) return;
      this.userInput = '';
      this.askQuestion(question, true);
    },
    retryLastQuestion() {
      const question = this.lastFailedQuestion;
      this.askQuestion(question, false);
    },
    isInternalSource(source) {
      return typeof source?.url === 'string'
        && source.url.startsWith('/')
        && !source.url.startsWith('//');
    },
    sourceHref(source) {
      const url = typeof source?.url === 'string' ? source.url.trim() : '';
      if (this.isInternalSource(source)) return url;
      try {
        const parsed = new URL(url);
        return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.toString() : '';
      } catch (error) {
        return '';
      }
    },
    sourceLabel(source) {
      if (source.section && source.section !== source.title) {
        return `${source.title} — ${source.section}`;
      }
      return source.title || 'Reckon knowledge';
    },
    openSource(source, event) {
      if (!this.isInternalSource(source)) return;
      event.preventDefault();
      if (this.$route.fullPath !== source.url) this.$router.push(source.url);
    },
  },
};
</script>

<style scoped>
.ai-assistant {
  bottom: 20px;
  position: fixed;
  right: 20px;
  z-index: 3000;
}

.assistant-toggle {
  backdrop-filter: blur(8px);
  background: rgba(10, 10, 10, .84) !important;
  border: 1px solid rgba(255, 255, 255, .18);
  border-radius: 10px;
  height: 60px !important;
  min-width: 60px !important;
  width: 60px !important;
  z-index: 3100;
}

.assistant-window {
  background: #0d0d0e;
  border: 1px solid #3a3a3d;
  border-radius: 14px;
  bottom: 92px;
  box-shadow: 0 28px 80px rgba(0, 0, 0, .58), 0 0 0 1px rgba(226, 50, 50, .08);
  display: flex;
  flex-direction: column;
  height: min(620px, calc(100vh - 112px));
  max-width: none !important;
  overflow: hidden;
  position: fixed;
  right: 20px;
  width: min(410px, calc(100vw - 40px));
}

.assistant-header {
  align-items: center;
  background:
    linear-gradient(115deg, rgba(226, 50, 50, .24), transparent 48%),
    linear-gradient(180deg, #1c1c1f, #111113);
  border-bottom: 1px solid rgba(226, 50, 50, .55);
  display: flex;
  justify-content: space-between;
  min-height: 76px;
  overflow: hidden;
  padding: 10px 10px 10px 12px;
  position: relative;
}

.assistant-header::before {
  background: #e23232;
  content: "";
  height: 3px;
  left: 12px;
  position: absolute;
  right: 12px;
  top: 0;
}

.assistant-header::after {
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, .08), transparent);
  content: "";
  height: 1px;
  left: 0;
  position: absolute;
  right: 0;
  top: 51%;
  transform: rotate(-7deg);
}

.assistant-header__brand,
.assistant-header__tools {
  align-items: center;
  display: flex;
  position: relative;
  z-index: 1;
}

.assistant-header__mark {
  align-items: center;
  background: linear-gradient(145deg, #e23232, #811010);
  border: 1px solid rgba(255, 255, 255, .28);
  border-radius: 10px;
  box-shadow: 0 8px 20px rgba(226, 50, 50, .24);
  display: flex;
  height: 44px;
  justify-content: center;
  margin-right: 11px;
  transform: rotate(-3deg);
  width: 44px;
}

.assistant-header__mark .v-icon {
  color: #fff;
  transform: rotate(3deg);
}

.assistant-header__identity {
  display: flex;
  flex-direction: column;
}

.assistant-header__identity strong {
  font-family: "Oswald", sans-serif;
  font-size: 1.45rem;
  letter-spacing: .04em;
  line-height: 1;
  text-transform: uppercase;
}

.assistant-header__identity small {
  color: #ff7171;
  font-size: .56rem;
  font-weight: 800;
  letter-spacing: .13em;
  margin-bottom: 3px;
  text-transform: uppercase;
}

.assistant-header__tools {
  gap: 2px;
}

.assistant-status {
  align-items: center;
  border: 1px solid #3c3c3f;
  border-radius: 999px;
  color: #bdbdbd;
  display: inline-flex;
  font-size: .58rem;
  font-weight: 800;
  gap: 5px;
  letter-spacing: .08em;
  margin-right: 4px;
  padding: 5px 8px;
  text-transform: uppercase;
}

.assistant-status i {
  background: #62d48b;
  border-radius: 50%;
  box-shadow: 0 0 8px rgba(98, 212, 139, .72);
  height: 6px;
  width: 6px;
}

.assistant-header__button {
  background: rgba(255, 255, 255, .04);
}

.assistant-body {
  background:
    linear-gradient(rgba(255, 255, 255, .018) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, .018) 1px, transparent 1px),
    radial-gradient(circle at 100% 0, rgba(226, 50, 50, .07), transparent 38%),
    #111113;
  background-size: 22px 22px, 22px 22px, auto, auto;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 0;
}

.messages {
  display: flex;
  flex-direction: column;
  gap: 14px;
  height: 100%;
  overflow-y: auto;
  padding: 18px 16px;
}

.message-row {
  display: flex;
  flex-direction: column;
  max-width: 88%;
}

.message-row--user {
  align-self: flex-end;
}

.message-row--assistant {
  align-self: flex-start;
}

.message {
  border-radius: 12px;
  line-height: 1.5;
  padding: 10px 14px;
  white-space: pre-wrap;
  word-break: break-word;
}

.message.user {
  background: linear-gradient(135deg, #c92222, #831111);
  border: 1px solid rgba(255, 255, 255, .12);
  border-bottom-right-radius: 4px;
  color: #fff;
}

.message.assistant {
  background: rgba(43, 43, 46, .94);
  border: 1px solid #3d3d41;
  border-left: 3px solid #e23232;
  border-bottom-left-radius: 4px;
  color: #fff;
}

.answer-label {
  align-items: center;
  color: #aaa;
  display: flex;
  font-size: .7rem;
  gap: 4px;
  margin: 6px 4px 0;
}

.message-sources {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 9px 4px 0;
}

.message-sources__title {
  color: #999;
  font-size: .7rem;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.message-source {
  color: #ff8b8b;
  font-size: .78rem;
  line-height: 1.35;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.message-source--plain {
  color: #bbb;
  text-decoration: none;
}

.assistant-thinking {
  align-items: center;
  display: flex;
  gap: 4px;
  min-height: 40px;
}

.assistant-thinking > span:not(.sr-only) {
  animation: thinking-pulse 1.2s infinite ease-in-out;
  background: #aaa;
  border-radius: 50%;
  height: 6px;
  width: 6px;
}

.assistant-thinking > span:nth-child(2) {
  animation-delay: .15s;
}

.assistant-thinking > span:nth-child(3) {
  animation-delay: .3s;
}

.assistant-error {
  font-size: .8rem;
  margin: 0;
}

.assistant-composer {
  align-items: flex-end;
  background: #0b0b0c;
  border-top: 1px solid #303034;
  display: flex;
  gap: 8px;
  padding: 10px 10px 5px;
}

.assistant-composer .v-textarea {
  flex: 1;
  max-height: 112px;
  overflow-y: auto;
}

.assistant-disclaimer {
  background: #0b0b0c;
  color: #858585;
  font-size: .66rem;
  margin: 0;
  padding: 2px 14px 9px;
  text-align: center;
}

.sr-only {
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
  clip: rect(0, 0, 0, 0);
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: opacity .2s ease, transform .2s ease;
}

.slide-up-enter,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(24px);
}

@keyframes thinking-pulse {
  0%, 70%, 100% { opacity: .35; transform: translateY(0); }
  35% { opacity: 1; transform: translateY(-3px); }
}

@media (max-width: 600px) {
  .ai-assistant {
    bottom: max(12px, env(safe-area-inset-bottom));
    right: 12px;
  }

  .assistant-window {
    border-radius: 12px;
    bottom: max(12px, env(safe-area-inset-bottom));
    height: auto;
    left: 12px;
    max-height: none;
    right: 12px;
    top: max(84px, env(safe-area-inset-top));
    width: auto;
  }

  .assistant-toggle--hidden {
    opacity: 0;
    pointer-events: none;
  }

  .assistant-header {
    flex: 0 0 auto;
    min-height: 72px;
  }

  .assistant-status {
    display: none;
  }

  .messages {
    padding: 14px 12px;
  }

  .message-row {
    max-width: 94%;
  }

  .assistant-composer {
    padding: 9px 8px 4px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .slide-up-enter-active,
  .slide-up-leave-active,
  .assistant-thinking > span:not(.sr-only) {
    animation: none;
    transition: none;
  }
}
</style>
